/**
 * ExtractDocumentViewer — the parsed-document reading surface, in the same
 * shape as the tpo-portal-frontend viewer:
 *
 *   ┌── top bar: list toggle · crumb (group › type · status) over the filename ──┐
 *   │           · Document / Split / Data · pager · download · close              │
 *   │ sidebar (group card › type › file, searchable) │ source │ detail tabs      │
 *   └──────────────────────────────────────────────────────────────────────────────┘
 *
 * The detail panel stacks the artifacts as TABS (Review · Structured · Text ·
 * JSON · Images) so each one gets the full column width. The view switch shows
 * the source alone, both, or the parsed data alone (the detail panel then takes
 * the full width).
 *
 * `ExtractTriPanel` is still exported from LoanExtractsViewer — swapping this
 * component back out for it is a one-line change in the callers.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSnackbar } from "notistack";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import ArrowBackRounded from "@mui/icons-material/ArrowBackRounded";
import ChevronLeftRounded from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRounded from "@mui/icons-material/ChevronRightRounded";
import CloseRounded from "@mui/icons-material/CloseRounded";
import ContentCopyOutlined from "@mui/icons-material/ContentCopyOutlined";
import DataObjectOutlined from "@mui/icons-material/DataObjectOutlined";
import DownloadOutlined from "@mui/icons-material/DownloadOutlined";
import ViewSidebarOutlined from "@mui/icons-material/ViewSidebarOutlined";

import Flx from "../../../../../components/layout/Flx";
import Txt from "../../../../../components/typography/Txt";
import colors, { amber, level1, level3, status, teal } from "../../../../../theme/colors";
import { losExtractsApi } from "../../../../../api/losExtractsApi";
import { documentStatusLabel } from "../../../../../constants/selectOptions/selectOptionsDocumentStatus";
import { getStatusStyle } from "../../../../../components/agGrid/cellRenderer/LoanFileStatusCell";
import { ExtractEditablePane, ImageThumb, SourcePane } from "./LoanExtractsViewer";
import { ReviewPane, reviewKindForDocType } from "./ExtractReviewPanes";
import StructuredTab from "./structured/StructuredTab";
import ExtractDocSidebar, {
  extractGroupOf,
  extractNameOf,
  extractParentOf,
  extractTypeOf,
} from "./ExtractDocSidebar";

// Structured tab: the new <StructuredTab/> (structured/, spec-driven) or the
// original flat FieldsPane below. Flip to false to go back — nothing else
// depends on it.
const USE_STRUCTURED_VIEW = true;

const SIDEBAR_WIDTH = 320;
// Detail panel (Structured · Text · JSON …). Scaled to the viewport so a 160px
// label column still leaves room for long values without crowding the source.
const PANEL_WIDTH = "clamp(520px, 40vw, 820px)";
const MONO = "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace";

// "federal_tax_id" / "federalTaxId" → "Federal tax id". Field keys come straight
// from the extraction schema, so humanize them rather than showing raw case.
const humanizeKey = (k) => {
  const s = String(k)
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .trim();
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
};

const isPlainObject = (v) =>
  v !== null && typeof v === "object" && !Array.isArray(v);

const scalarText = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  return String(v);
};

// ── focusField matching ──────────────────────────────────────────────────────
// Callers (e.g. an AI finding's evidence) hand us the raw extraction path they
// flagged — "assessment.is_tax_delinquent", "named_insured", "comparableSales".
// The Structured tab shows humanized, dot-joined labels ("Assessment · Is tax
// delinquent"), so both sides are normalized to lowercase word tokens and
// compared loosely: a row matches when its label equals the field, ends with it
// (the leaf of a deeper path), or starts with it (an array group's rows).
const normalizeFieldKey = (s) =>
  String(s || "")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2") // camelCase → words
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

const rowMatchesField = (label, normField) => {
  if (!normField) return false;
  const r = normalizeFieldKey(label);
  return (
    r === normField ||
    r.endsWith(` ${normField}`) ||
    r.startsWith(`${normField} `)
  );
};

// Flatten the structured JSON into { title, rows[] } groups for the Structured
// tab. Each top-level object becomes its own group; scalars/arrays at the root
// are collected under "Document". Nested objects flatten with a dotted label
// and arrays of objects get one row per entry, so nothing is silently dropped.
const toFieldGroups = (json) => {
  if (!isPlainObject(json)) return [];

  const leafRows = (obj, prefix = "") => {
    const rows = [];
    for (const [k, v] of Object.entries(obj)) {
      const label = prefix ? `${prefix} · ${humanizeKey(k)}` : humanizeKey(k);
      if (isPlainObject(v)) {
        rows.push(...leafRows(v, label));
      } else if (Array.isArray(v)) {
        if (v.every((x) => !isPlainObject(x))) {
          rows.push({ label, value: v.length ? v.map(scalarText).join(", ") : "—" });
        } else {
          v.forEach((item, i) => {
            if (isPlainObject(item)) rows.push(...leafRows(item, `${label} ${i + 1}`));
            else rows.push({ label: `${label} ${i + 1}`, value: scalarText(item) });
          });
        }
      } else {
        rows.push({ label, value: scalarText(v) });
      }
    }
    return rows;
  };

  const groups = [];
  const rootRows = [];
  for (const [k, v] of Object.entries(json)) {
    if (isPlainObject(v)) {
      const rows = leafRows(v);
      if (rows.length) groups.push({ title: humanizeKey(k), rows });
    } else if (Array.isArray(v) && v.some(isPlainObject)) {
      const rows = leafRows({ [k]: v });
      if (rows.length) groups.push({ title: humanizeKey(k), rows });
    } else {
      rootRows.push(...leafRows({ [k]: v }));
    }
  }
  if (rootRows.length) groups.unshift({ title: "Document", rows: rootRows });
  return groups;
};

// ── Status badge (top bar crumb) ─────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  if (!status) return null;
  const { bg, fg } = getStatusStyle(status);
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        height: 20,
        px: 1,
        borderRadius: "999px",
        fontSize: 11,
        fontWeight: 700,
        color: fg,
        backgroundColor: bg,
        whiteSpace: "nowrap",
        flexShrink: 0,
      }}
    >
      {documentStatusLabel(status)}
    </Box>
  );
};

const PaneEmptyState = ({ text }) => (
  <Flx column ac jc g={1.25} sx={{ py: 7, px: 4, textAlign: "center" }}>
    <Flx
      ac
      jc
      sx={{ width: 44, height: 44, borderRadius: "12px", backgroundColor: colors.slate100 }}
    >
      <DataObjectOutlined sx={{ fontSize: 20, color: colors.slate500 }} />
    </Flx>
    <Txt sx={{ fontSize: 13, color: colors.slate500, maxWidth: 260 }}>{text}</Txt>
  </Flx>
);

// Which panes the body shows: the source file alone, both, or the parsed data
// alone. A level-3 toggle — only the presentation changes.
const VIEWS = [
  { key: "document", label: "Document" },
  { key: "split", label: "Split" },
  { key: "data", label: "Data" },
];

const ViewSwitch = ({ value, onChange }) => (
  <Flx
    ac
    sx={{
      flexShrink: 0,
      p: "3px",
      gap: "2px",
      borderRadius: "8px",
      border: `1px solid ${colors.slate200}`,
      backgroundColor: level3.track,
    }}
  >
    {VIEWS.map((v) => {
      const active = v.key === value;
      return (
        <Box
          key={v.key}
          component="button"
          type="button"
          onClick={() => onChange(v.key)}
          aria-pressed={active}
          sx={{
            border: 0,
            font: "inherit",
            cursor: "pointer",
            px: 1.25,
            py: 0.5,
            borderRadius: "6px",
            fontSize: 12,
            fontWeight: active ? 600 : 500,
            color: active ? level3.activeText : level3.restText,
            backgroundColor: active ? level3.activeBg : "transparent",
            boxShadow: active ? level3.activeShadow : "none",
            "&:hover": { color: level3.activeText },
          }}
        >
          {v.label}
        </Box>
      );
    })}
  </Flx>
);

// ── Structured tab — the structured JSON as labelled key/value rows ──────────
function FieldsPane({ dealId, extract, focusField }) {
  const { enqueueSnackbar } = useSnackbar();
  const [json, setJson] = useState(undefined);
  const [loading, setLoading] = useState(true);
  const blobName = extract?.blob_name;
  // First row matching `focusField` — scrolled into view once the JSON lands.
  const focusRowRef = useRef(null);
  const normFocus = useMemo(() => normalizeFieldKey(focusField), [focusField]);

  useEffect(() => {
    if (!dealId || !blobName) return undefined;
    let active = true;
    setLoading(true);
    losExtractsApi
      .getText(dealId, blobName)
      .then((t) => {
        if (!active) return;
        try {
          setJson(typeof t === "string" ? JSON.parse(t) : t);
        } catch {
          setJson(undefined);
        }
      })
      .catch(() => active && setJson(undefined))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [dealId, blobName]);

  // Rows carry their own focus state so the render stays a plain map. Only the
  // first match anchors the scroll — a leaf name can repeat across array entries.
  const groups = useMemo(() => {
    const gs = toFieldGroups(json);
    if (!normFocus) return gs;
    let anchored = false;
    return gs.map((g) => ({
      ...g,
      rows: g.rows.map((r) => {
        const focused = rowMatchesField(r.label, normFocus);
        const focusAnchor = focused && !anchored;
        if (focusAnchor) anchored = true;
        return { ...r, focused, focusAnchor };
      }),
    }));
  }, [json, normFocus]);

  // Scroll the flagged field into view after render. Depends on the parsed JSON
  // rather than `loading` so it also fires when the doc/field changes in place.
  useEffect(() => {
    if (!normFocus || !json) return;
    focusRowRef.current?.scrollIntoView({ block: "center" });
  }, [normFocus, json]);

  const copy = useCallback(
    (value) => {
      navigator.clipboard?.writeText(String(value));
      enqueueSnackbar("Copied", { variant: "success" });
    },
    [enqueueSnackbar],
  );

  if (loading) {
    return (
      <Flx jc sx={{ py: 6 }}>
        <CircularProgress size={20} />
      </Flx>
    );
  }
  if (!groups.length) {
    return <PaneEmptyState text="No structured fields were extracted from this document." />;
  }

  return (
    <Box sx={{ p: 2 }}>
      {groups.map((g) => (
        <Box key={g.title} sx={{ mb: 2.25 }}>
          <Txt
            component="div"
            sx={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              color: colors.slate500,
              pb: 0.9,
              mb: 0.25,
              borderBottom: `1px solid ${colors.slate100}`,
            }}
          >
            {g.title}
          </Txt>
          {g.rows.map((r, i) => (
            <Box
              key={`${r.label}-${i}`}
              ref={r.focusAnchor ? focusRowRef : undefined}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.25,
                px: 1,
                py: 0.9,
                borderRadius: "7px",
                // The field an AI finding cited — amber so it reads as "this is
                // what was flagged", distinct from the gray hover state.
                ...(r.focused && {
                  backgroundColor: amber.light,
                  boxShadow: `inset 3px 0 0 ${amber.main}`,
                }),
                "&:hover": { backgroundColor: r.focused ? amber.light : colors.slate50 },
                "&:hover .copy-btn": { opacity: 1 },
              }}
            >
              <Txt
                sx={{
                  width: 160,
                  flexShrink: 0,
                  fontSize: 12.5,
                  color: r.focused ? amber.chipText : colors.slate500,
                  fontWeight: r.focused ? 600 : 400,
                  overflowWrap: "anywhere",
                }}
              >
                {r.label}
              </Txt>
              <Txt
                sx={{
                  flex: 1,
                  minWidth: 0,
                  fontFamily: MONO,
                  fontSize: 12.5,
                  color: colors.slate900,
                  overflowWrap: "anywhere",
                }}
              >
                {r.value}
              </Txt>
              <Tooltip title="Copy">
                <IconButton
                  className="copy-btn"
                  size="small"
                  onClick={() => copy(r.value)}
                  sx={{ opacity: 0, transition: "opacity .12s", p: 0.5 }}
                >
                  <ContentCopyOutlined sx={{ fontSize: 13 }} />
                </IconButton>
              </Tooltip>
            </Box>
          ))}
        </Box>
      ))}
    </Box>
  );
}

// ── Main ─────────────────────────────────────────────────────────────────────
export default function ExtractDocumentViewer({
  dealId,
  documents = [],
  selectedId,
  onSelectId,
  onClose,
  // Optional extraction path (e.g. an AI finding's `evidence[].extractedField`)
  // to highlight and scroll to in the Structured tab.
  focusField,
}) {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [tab, setTab] = useState("fields");
  const [view, setView] = useState("split");
  const [downloading, setDownloading] = useState(false);
  // "Show in text" from the Structured tab → Text tab + find-in-markdown.
  const [textSearch, setTextSearch] = useState(null);

  const index = useMemo(
    () => documents.findIndex((d) => d.parent_document_id === selectedId),
    [documents, selectedId],
  );
  const selected = index >= 0 ? documents[index] : null;
  const parent = extractParentOf(selected);
  const extracts = selected?.extracts || {};
  const markdowns = extracts.markdown || [];
  const jsons = extracts.structured_json || [];
  const images = extracts.image || [];

  // Some docTypes (Note, Photo ID, Background Report) land richer structured
  // extracts with a dedicated review surface — see ExtractReviewPanes.
  const docType = extractTypeOf(selected);
  const reviewKind = reviewKindForDocType(docType);

  // Land on a tab that actually has content for this document. Prefer the
  // specialized Review tab when this docType has one.
  useEffect(() => {
    if (reviewKind) setTab("review");
    else if (jsons.length) setTab("fields");
    else if (markdowns.length) setTab("text");
    else if (images.length) setTab("images");
    else setTab("fields");
    setTextSearch(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  const showInText = useCallback((query) => {
    setTextSearch({ query, nonce: Date.now() });
    setTab("text");
  }, []);

  const goto = (delta) => {
    const next = documents[index + delta];
    if (next) onSelectId(next.parent_document_id);
  };

  // Download the SOURCE file (the thing being previewed), via the same blob URL
  // the source pane uses.
  const handleDownload = useCallback(() => {
    const fsn = parent?.file_storage_name;
    if (!dealId || !fsn || downloading) return;
    setDownloading(true);
    losExtractsApi
      .getSourceBlobUrl(dealId, fsn)
      .then((url) => {
        const a = document.createElement("a");
        a.href = url;
        a.download = parent.file_display_name || fsn;
        document.body.appendChild(a);
        a.click();
        a.remove();
        // Give the browser a tick to start the download before revoking.
        setTimeout(() => URL.revokeObjectURL(url), 10000);
      })
      .finally(() => setDownloading(false));
  }, [dealId, parent?.file_storage_name, parent?.file_display_name, downloading]);

  const TABS = [
    ...(reviewKind ? [{ key: "review", label: "Review" }] : []),
    { key: "fields", label: "Structured" },
    { key: "text", label: "Text" },
    { key: "json", label: "JSON" },
    { key: "images", label: "Images", count: images.length },
  ];

  // Where the structured output stands: its version, and whether a person has
  // edited it since the extractor wrote it.
  const current = jsons[0];
  const provenance = [
    current?.version ? `extract v${current.version}` : null,
    current?.edited ? "edited" : null,
    selected?.source_kind === "agent_run"
      ? "agent run report"
      : selected?.source_kind === "generated"
        ? "generated"
        : null,
  ].filter(Boolean);

  const crumbGroup = extractGroupOf(selected);
  const crumbType = docType;
  const crumbStatus = selected?.parent?.documentStatus || null;
  const rejection = selected?.parent?.extractionRejection || null;

  return (
    <Flx column sx={{ height: "100%", minHeight: 0, backgroundColor: colors.slate50 }}>
      {/* ── Top bar: list toggle · crumb + name · view · pager · download · close ── */}
      <Flx
        ac
        g={1.5}
        sx={{
          height: 60,
          flexShrink: 0,
          px: 2,
          backgroundColor: colors.white,
          borderBottom: `1px solid ${colors.slate200}`,
        }}
      >
        {onClose && (
          <Tooltip title="Back">
            <IconButton
              size="small"
              onClick={onClose}
              aria-label="Back"
              sx={{ flexShrink: 0, mr: -0.75, color: colors.slate500 }}
            >
              <ArrowBackRounded sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        )}

        <Tooltip title={sidebarOpen ? "Hide document list" : "Show document list"}>
          <IconButton
            size="small"
            onClick={() => setSidebarOpen((v) => !v)}
            aria-pressed={sidebarOpen}
            sx={{
              width: 32,
              height: 32,
              flexShrink: 0,
              borderRadius: "8px",
              border: `1px solid ${sidebarOpen ? teal[200] : colors.slate200}`,
              color: sidebarOpen ? teal[700] : colors.slate500,
              backgroundColor: sidebarOpen ? teal[50] : colors.white,
            }}
          >
            <ViewSidebarOutlined sx={{ fontSize: 17, transform: "scaleX(-1)" }} />
          </IconButton>
        </Tooltip>

        <Flx column sx={{ minWidth: 0, flex: 1, gap: "1px" }}>
          <Flx ac g={0.75} sx={{ minWidth: 0 }}>
            {crumbGroup ? (
              <Txt noWrap sx={{ fontSize: 11.5, color: colors.slate500 }}>
                {crumbGroup}
              </Txt>
            ) : null}
            {crumbGroup && crumbType ? (
              <Txt sx={{ fontSize: 11.5, color: colors.slate400 }}>›</Txt>
            ) : null}
            {crumbType ? (
              <Txt noWrap sx={{ fontSize: 11.5, color: colors.slate500 }}>
                {crumbType}
              </Txt>
            ) : null}
            <StatusBadge status={crumbStatus} />
          </Flx>
          <Txt noWrap sx={{ fontWeight: 600, fontSize: 14, color: colors.slate900, minWidth: 0 }}>
            {selected ? extractNameOf(selected) : "No document selected"}
          </Txt>
        </Flx>

        <ViewSwitch value={view} onChange={setView} />

        {documents.length > 1 && (
          <Flx ac sx={{ color: colors.slate500, fontSize: 12, flexShrink: 0 }}>
            <Tooltip title="Previous document">
              <span>
                <IconButton size="small" disabled={index <= 0} onClick={() => goto(-1)}>
                  <ChevronLeftRounded sx={{ fontSize: 16 }} />
                </IconButton>
              </span>
            </Tooltip>
            <Box sx={{ px: 0.5, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
              {index >= 0 ? index + 1 : "—"} of {documents.length}
            </Box>
            <Tooltip title="Next document">
              <span>
                <IconButton
                  size="small"
                  disabled={index < 0 || index >= documents.length - 1}
                  onClick={() => goto(1)}
                >
                  <ChevronRightRounded sx={{ fontSize: 16 }} />
                </IconButton>
              </span>
            </Tooltip>
          </Flx>
        )}

        <Button
          size="small"
          variant="outlined"
          color="gray"
          startIcon={<DownloadOutlined sx={{ fontSize: 15 }} />}
          onClick={handleDownload}
          disabled={!parent?.file_storage_name || downloading}
          sx={{ flexShrink: 0, textTransform: "none", fontWeight: 600 }}
        >
          Download
        </Button>

        {onClose && (
          <Tooltip title="Close">
            <IconButton size="small" onClick={onClose} sx={{ flexShrink: 0, color: colors.slate500 }}>
              <CloseRounded sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        )}
      </Flx>

      {/* ── Body: sidebar · source · detail ── */}
      <Flx sx={{ flex: 1, minHeight: 0 }}>
        {sidebarOpen && (
          <ExtractDocSidebar
            documents={documents}
            selectedId={selectedId}
            onSelectId={onSelectId}
            width={SIDEBAR_WIDTH}
          />
        )}

        {view !== "data" && (
          <Flx column sx={{ flex: 1, minWidth: 0, p: 2 }}>
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                borderRadius: "10px",
                border: `1px solid ${colors.slate200}`,
                backgroundColor: colors.white,
                boxShadow: "0 1px 2px rgba(14,23,38,.05), 0 4px 14px rgba(14,23,38,.05)",
                overflow: "hidden",
              }}
            >
              {!selected ? (
                <PaneEmptyState text="Pick a document from the list to preview it." />
              ) : selected.source_kind && selected.source_kind !== "document" ? (
                <PaneEmptyState text="This parsed output has no matching document on the loan, so there is no source file to show." />
              ) : (
                <SourcePane dealId={dealId} parent={parent} />
              )}
            </Box>
          </Flx>
        )}

        {view !== "document" && (
          <Flx
            column
            sx={{
              width: view === "data" ? "auto" : PANEL_WIDTH,
              flex: view === "data" ? 1 : "0 0 auto",
              minWidth: 0,
              backgroundColor: colors.white,
              borderLeft: `1px solid ${colors.slate200}`,
              minHeight: 0,
            }}
          >
            <Flx
              g={0.25}
              sx={{ px: 1.75, pt: 1.25, flexShrink: 0, borderBottom: `1px solid ${colors.slate200}` }}
            >
              {TABS.map((t) => {
                const active = tab === t.key;
                return (
                  <Flx
                    key={t.key}
                    ac
                    g={0.75}
                    onClick={() => setTab(t.key)}
                    sx={{
                      px: 1.5,
                      pb: 1.25,
                      pt: 1,
                      mb: "-1px",
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: "pointer",
                      color: active ? level1.text : level1.restText,
                      borderBottom: `2px solid ${active ? level1.indicator : "transparent"}`,
                      "&:hover": { color: active ? level1.text : colors.slate700 },
                    }}
                  >
                    {t.label}
                    {t.count ? (
                      <Box
                        component="span"
                        sx={{
                          fontSize: 10.5,
                          fontWeight: 600,
                          borderRadius: "999px",
                          px: 0.85,
                          py: "1px",
                          backgroundColor: active ? level1.tint : colors.slate100,
                          color: active ? level1.text : colors.slate500,
                        }}
                      >
                        {t.count}
                      </Box>
                    ) : null}
                  </Flx>
                );
              })}
              {provenance.length > 0 && (
                <Txt
                  noWrap
                  sx={{
                    ml: "auto",
                    alignSelf: "center",
                    pb: 0.5,
                    fontSize: 11.5,
                    color: colors.slate400,
                    minWidth: 0,
                  }}
                >
                  {provenance.join(" · ")}
                </Txt>
              )}
            </Flx>

            {rejection ? (
              <Box
                role="alert"
                sx={{
                  mx: 1.75,
                  mt: 1.25,
                  px: 1.5,
                  py: 1,
                  borderRadius: "8px",
                  fontSize: 12.5,
                  color: status.error.fg,
                  backgroundColor: status.error.bg,
                }}
              >
                <Box component="span" sx={{ fontWeight: 600 }}>Extraction was rejected.</Box>
                {rejection.reason ? ` ${rejection.reason}` : ""}
                {rejection.documentType ? ` The extractor thinks this is a ${rejection.documentType}.` : ""}
              </Box>
            ) : null}

            <Flx column sx={{ flex: 1, minHeight: 0 }}>
              {!selected ? null : tab === "review" ? (
                <ReviewPane
                  kind={reviewKind}
                  dealId={dealId}
                  extract={jsons[0]}
                  images={images}
                />
              ) : tab === "fields" && USE_STRUCTURED_VIEW ? (
                <StructuredTab
                  dealId={dealId}
                  row={selected}
                  focusField={focusField}
                  onShowInText={showInText}
                />
              ) : tab === "fields" ? (
                <Box sx={{ flex: 1, overflowY: "auto", minHeight: 0 }}>
                  {jsons.length ? (
                    <FieldsPane dealId={dealId} extract={jsons[0]} focusField={focusField} />
                  ) : (
                    <PaneEmptyState text="No structured output for this document." />
                  )}
                </Box>
              ) : tab === "text" ? (
                markdowns.length ? (
                  <ExtractEditablePane
                    key={markdowns[0].blob_name}
                    dealId={dealId}
                    extract={markdowns[0]}
                    kind="markdown"
                    searchRequest={textSearch}
                  />
                ) : (
                  <PaneEmptyState text="No layout text is available for this document." />
                )
              ) : tab === "json" ? (
                jsons.length ? (
                  <ExtractEditablePane
                    key={jsons[0].blob_name}
                    dealId={dealId}
                    extract={jsons[0]}
                    kind="json"
                  />
                ) : (
                  <PaneEmptyState text="No structured output for this document." />
                )
              ) : (
                <Box sx={{ flex: 1, overflowY: "auto", minHeight: 0, p: 1.75 }}>
                  {images.length ? (
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fill, minmax(130px, 1fr))",
                        gap: 1,
                      }}
                    >
                      {images.map((img) => (
                        <ImageThumb key={img.blob_name} dealId={dealId} img={img} />
                      ))}
                    </Box>
                  ) : (
                    <PaneEmptyState text="No images were found in this document." />
                  )}
                </Box>
              )}
            </Flx>
          </Flx>
        )}
      </Flx>
    </Flx>
  );
}
